import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MagnifyingGlassIcon, QrCodeIcon, UserPlusIcon } from "@heroicons/react/24/outline";

/* --- Components --- */
import BookCard from "../../components/dashboard/BookCard";
import Loader from "../../components/common/Loader";
import MemberCheckout from "../../components/checkout/MemberCheckout";
import SearchableMemberSelect from "../../components/common/SearchableMemberSelect";

/* --- Modals --- */
import ScannerModal from "../../components/modals/ScannerModal";
import ConfirmMemberModal from "../../components/modals/ConfirmMemberModal";
import DamageModal from "../../components/modals/DamageModal";
import BookHistoryModal from "../../components/modals/BookHistoryModal";

/* --- API --- */
import {
    fetchProducts,
    searchProducts,
    getMembers,
    getBookShelfByUser,
    addMember,
    getMemberById,
    getUserCart,
    checkoutOrder,
} from "../../api/productApi";

/* ==================================================================================
 * CACHE HELPERS
 * ================================================================================== */
const CACHE_KEYS = {
    BOOKS: "sq_books_cache_v2",
    MEMBERS: "sq_members_cache_v2",
    SHELVES: "sq_shelves_cache_map_v1"
};

const loadCache = (key) => {
    try {
        const cached = localStorage.getItem(key);
        return cached ? JSON.parse(cached) : (key === CACHE_KEYS.SHELVES ? {} : []);
    } catch (e) {
        return key === CACHE_KEYS.SHELVES ? {} : [];
    }
};

const saveCache = (key, data) => {
    try {
        localStorage.setItem(key, JSON.stringify(data));
    } catch (e) {
        console.warn("Cache save failed", e);
    }
};

const updateShelfCache = (memberId, info, shelf, cart) => {
    const currentCache = loadCache(CACHE_KEYS.SHELVES);
    currentCache[memberId] = {
        info,
        shelf,
        cart,
        timestamp: Date.now()
    };
    saveCache(CACHE_KEYS.SHELVES, currentCache);
};

/* ==================================================================================
 * CUSTOM HOOKS
 * ================================================================================== */
const useBooks = (token) => {
    const [products, setProducts] = useState(() => loadCache(CACHE_KEYS.BOOKS));
    const [loading, setLoading] = useState(() => loadCache(CACHE_KEYS.BOOKS).length === 0);
    const [loadingMore, setLoadingMore] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [query, setQuery] = useState("");

    const visibleProducts = useMemo(() => products, [products]);

    useEffect(() => {
        if (!token) return;
        let active = true;
        const load = async () => {
            if (query) setLoading(true);
            try {
                const apiCall = query
                    ? () => searchProducts({ query, token, page: 1, perPage: 100 })
                    : () => fetchProducts({ token, page: 1, perPage: 100 });
                const res = await apiCall();
                if (!active) return;
                const mapped = (res.data || []).map(mapProductToBook);
                setProducts(mapped);
                if (!query) saveCache(CACHE_KEYS.BOOKS, mapped);
                setPage(res.pagination?.current_page || 1);
                setTotalPages(res.pagination?.total_pages || 1);
            } catch (e) { console.error(e); } finally { if (active) setLoading(false); }
        };
        const t = setTimeout(load, 350);
        return () => { active = false; clearTimeout(t); };
    }, [token, query]);

    const loadMore = useCallback(async () => {
        if (loadingMore || query || page >= totalPages) return;
        setLoadingMore(true);
        try {
            const res = await fetchProducts({ token, page: page + 1, perPage: 100 });
            const mapped = (res.data || []).map(mapProductToBook);
            setProducts(prev => {
                const ids = new Set(prev.map(p => p.id));
                const merged = [...prev, ...mapped.filter(x => !ids.has(x.id))];
                if (!query) saveCache(CACHE_KEYS.BOOKS, merged);
                return merged;
            });
            setPage(res.pagination?.current_page);
        } catch (e) { console.error(e); } finally { setLoadingMore(false); }
    }, [loadingMore, query, page, totalPages, token]);

    return { products: visibleProducts, loading, loadingMore, loadMore, query, setQuery };
};

const useMembers = (token) => {
    const [members, setMembers] = useState(() => loadCache(CACHE_KEYS.MEMBERS));
    const [loading, setLoading] = useState(() => loadCache(CACHE_KEYS.MEMBERS).length === 0);
    const [error, setError] = useState("");

    const fetchMembers = useCallback(async () => {
        if (!token) return;
        setError("");
        try {
            const res = await getMembers({ token });
            const mapped = (res || []).map(m => ({
                id: m.id,
                name: m.member_name,
                memberId: m.id,
                email: m.email,
                phone: m.phone,
                address: m.address,
            }));
            setMembers(mapped);
            saveCache(CACHE_KEYS.MEMBERS, mapped);
        } catch (e) { setError("Failed to load members"); } finally { setLoading(false); }
    }, [token]);

    useEffect(() => { fetchMembers(); }, [fetchMembers]);

    return { members, loading, error, setMembers };
};

/* ==================================================================================
 * HELPER: Mapper
 * ================================================================================== */
const mapProductToBook = (p) => ({
    id: p.id,
    title: p.name,
    img: p.image || "https://via.placeholder.com/120x180",
    credits: p.price ?? "-",
    stock_status: p.stock_status ?? "Available",
    smart_product_id: p.smart_product_id,
    activeOrder: p.active_order || null,
    copies: Array.isArray(p.copies) ? p.copies.map(c => ({ id: c.sq_copy_id, label: c.copys })) : [],
});

/* ==================================================================================
 * COMPONENT: MemberListItem
 * ================================================================================== */
const MemberListItem = React.memo(({ member, onSelect }) => (
    <button
        type="button"
        onClick={() => onSelect(member)}
        className="w-full text-left border rounded-lg p-4 mb-3 bg-white hover:bg-gray-50 transition shadow-sm group active:bg-gray-100"
    >
        <div className="flex justify-between mb-2">
            <div className="text-sm font-medium text-gray-900 group-hover:text-black">
                <span className="text-gray-500 font-normal">Member:</span> {member.name}
            </div>
            <div className="text-xs font-semibold bg-gray-100 px-2 py-1 rounded text-gray-600">
                ID: {member.memberId}
            </div>
        </div>
        <div className="text-xs text-gray-500 space-y-1">
            <div className="flex gap-2"><span className="w-16 text-gray-400">Phone:</span><span>{member.phone || "-"}</span></div>
            <div className="flex gap-2"><span className="w-16 text-gray-400">Email:</span><span className="truncate">{member.email || "-"}</span></div>
            <div className="flex gap-2"><span className="w-16 text-gray-400">Address:</span><span className="truncate flex-1">{member.address || "-"}</span></div>
        </div>
    </button>
));

const parseAddress = (addressStr, fallbackPostcode = "") => {
    const res = {
        address_1: "",
        city: "Sample City",
        state: "Karnataka",
        postcode: fallbackPostcode ? String(fallbackPostcode).trim() : "560001",
        country: "IN"
    };
    if (!addressStr || typeof addressStr !== "string") {
        if (res.postcode) {
            res.address_1 = `Pincode: ${res.postcode}`;
        } else {
            res.address_1 = "Default Address";
        }
        return res;
    }
    const parts = addressStr.split(",").map(p => p.trim()).filter(Boolean);
    if (parts.length > 0) {
        res.address_1 = parts[0];
    }
    if (parts.length > 1) {
        res.city = parts[1];
    }
    if (parts.length > 2) {
        res.state = parts[2];
    }
    if (parts.length > 3) {
        res.postcode = parts[3];
    }
    if (parts.length > 4) {
        res.country = parts[4];
    }
    
    if (!res.postcode || !/^\d+$/.test(res.postcode)) {
        const pinMatch = addressStr.match(/\b\d{5,6}\b/);
        if (pinMatch) {
            res.postcode = pinMatch[0];
        } else if (fallbackPostcode) {
            res.postcode = String(fallbackPostcode).trim();
        }
    }
    
    if (!res.address_1) {
        res.address_1 = addressStr;
    }
    return res;
};

/* ==================================================================================
 * MAIN PAGE COMPONENT
 * ================================================================================== */
export default function ManageMemberPage() {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    const location = useLocation();

    // --- Custom Hooks ---
    const { products, loading: booksLoading, loadingMore, loadMore, query: bookQuery, setQuery: setBookQuery } = useBooks(token);
    const { members: rawMembers, loading: membersLoading, error: membersError, setMembers } = useMembers(token);

    // --- Local State ---
    const [selectedMember, setSelectedMember] = useState(null);
    const [damageMember, setDamageMember] = useState(null);
    const [memberInfo, setMemberInfo] = useState(null);
    const [shelfItems, setShelfItems] = useState([]);
    const [cartItems, setCartItems] = useState([]);

    const [isSyncing, setIsSyncing] = useState(false);

    const [walkInType, setWalkInType] = useState("WALK_IN");
    const [checkoutLoading, setCheckoutLoading] = useState(false);

    // --- Modals State ---
    const [activeModal, setActiveModal] = useState({ type: null, data: null });
    const [toast, setToast] = useState("");
    const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);
    const [newMember, setNewMember] = useState({ first_name: "", last_name: "", email: "", phone: "" });
    const [addingMember, setAddingMember] = useState(false);
    const [addMemberStatus, setAddMemberStatus] = useState({ msg: "", error: "" });

    const listContainerRef = useRef(null);

    // --- Helpers ---
    const showToast = useCallback((msg) => {
        setToast(msg);
        setTimeout(() => setToast(""), 2000);
    }, []);

    const closeModal = () => setActiveModal({ type: null, data: null });

    /* --------------------------------------------------------------------------
     * INSTANT MEMBER SELECT HANDLER
     * -------------------------------------------------------------------------- */
    const handleMemberSelect = useCallback(async (member) => {
        // 1. INSTANT VIEW SWITCH
        setSelectedMember(member);
        setCartItems([]);

        // 2. CHECK CACHE
        const shelfCache = loadCache(CACHE_KEYS.SHELVES);
        const cachedMemberData = shelfCache[member.id];

        if (cachedMemberData) {
            setMemberInfo(cachedMemberData.info);
            setShelfItems(cachedMemberData.shelf);
            setCartItems(cachedMemberData.cart || []);
            setIsSyncing(true); // Sync silently
        } else {
            // No Cache: Show basic info
            setMemberInfo({
                name: member.name,
                email: member.email,
                phone: member.phone,
                credits: "-",
                overdue_charges_details: [],
            });
            setShelfItems([]);
            setCartItems([]);
            setIsSyncing(true);
        }

        // 3. BACKGROUND FETCH (Silent Sync)
        try {
            const [infoRes, shelfRes, cartRes] = await Promise.all([
                getMemberById({ token, userId: member.id }),
                getBookShelfByUser({ userId: member.id, token }),
                getUserCart({ token, userId: member.id }).catch(err => {
                    console.error("Cart fetch failed", err);
                    return { success: true, data: [] };
                })
            ]);

            const info = infoRes?.data?.member_information || {};

            const freshInfo = {
                name: info.member_name,
                phone: info.phone_number,
                email: info.email,
                dob: info.date_of_birth,
                subscription: info.subscription_plan,
                deposit: info.deposit,
                damageCharges: info.damage_charges,
                overdue_charges: info.overdue_charges,
                overdue_charges_details: info.overdue_charges_details || infoRes?.data?.overdue_charges_details || [],
                billing_address: info.billing_address,
                pin_code: info.pin_code,
                credits: infoRes?.data?.credits ?? 0,
            };

            const cartBooks = cartRes?.data || [];
            const freshCart = cartBooks.map(item => ({
                id: Number(item.product_id),
                title: item.name,
                img: item.image,
                credits: Number(item.price || 0),
                status: item.issue_or_return === "return" ? "Returning" : "Issuing",
                smart_product_id: item.smart_product_id,
            }));

            const returningIds = new Set(
                freshCart.filter(c => c.status === "Returning").map(c => c.id)
            );

            const books = shelfRes?.data?.books || [];
            const freshShelf = books.map(b => {
                const bookId = Number(b.product_id);
                const isInCart = returningIds.has(bookId);
                return {
                    ...b,
                    id: bookId,
                    title: b.book_name,
                    img: b.book_img_url,
                    credits: Number(b.credits || 0),
                    status: isInCart ? "In Cart" : "Reading",
                    copyId: b?.copy_obj?.copy_id || b.copy_post_id,
                };
            });

            // Update State & Cache
            setMemberInfo(freshInfo);
            setShelfItems(freshShelf);
            setCartItems(freshCart);
            updateShelfCache(member.id, freshInfo, freshShelf, freshCart);

        } catch (e) {
            console.error(e);
            if (!cachedMemberData) showToast("Failed to load shelf data");
        } finally {
            setIsSyncing(false);
        }
    }, [token, showToast]);

    // --- Auto select member from location state ---
    useEffect(() => {
        if (location.state?.memberId && rawMembers.length > 0) {
            const m = rawMembers.find(member => String(member.id) === String(location.state.memberId));
            if (m) {
                handleMemberSelect(m);
            }
        }
    }, [location.state, rawMembers, handleMemberSelect]);

    const handleBookSelect = useCallback((book) => {
        if (!selectedMember?.id) {
            showToast("Please select a member first 👤");
            return;
        }
        if (book?.stock_status === "outofstock") {
            showToast("Cannot add out of stock books to cart");
            return;
        }
        setActiveModal({ type: 'CONFIRM', data: book });
    }, [selectedMember, showToast]);

    const handleBookIconClick = useCallback((book, view) => {
        if (view === "BOOK_HISTORY") setActiveModal({ type: 'HISTORY', data: book });
        else if (view === "BOOK_DAMAGE") setActiveModal({ type: 'DAMAGE', data: book });
        else if (["BOOK_INFO", "BOOK_SETTINGS"].includes(view)) handleBookSelect(book);
    }, [handleBookSelect]);

    // --- Infinite Scroll ---
    useEffect(() => {
        const el = listContainerRef.current;
        if (!el) return;
        const onScroll = () => {
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 120) loadMore();
        };
        el.addEventListener("scroll", onScroll);
        return () => el.removeEventListener("scroll", onScroll);
    }, [loadMore]);

    // --- Add Member Logic ---
    const handleAddMember = async () => {
        setAddMemberStatus({ msg: "", error: "" });
        const { first_name, last_name, email, phone } = newMember;
        if (!first_name || !last_name || !email || !phone) {
            return setAddMemberStatus({ error: "All fields required", msg: "" });
        }

        setAddingMember(true);
        try {
            const res = await addMember({ token, payload: { email, first_name, last_name, phone } });
            if (res?.success === false) throw new Error(res?.message);

            setAddMemberStatus({ msg: "Member added!", error: "" });

            const newM = {
                id: res.data?.id || Date.now(),
                name: `${first_name} ${last_name}`,
                memberId: res.data?.id,
                email, phone, address: ""
            };

            setMembers(prev => [newM, ...prev]);
            saveCache(CACHE_KEYS.MEMBERS, [newM, ...rawMembers]);
            handleMemberSelect(newM);

            setTimeout(() => {
                setIsAddMemberOpen(false);
                setNewMember({ first_name: "", last_name: "", email: "", phone: "" });
                setAddMemberStatus({ msg: "", error: "" });
            }, 800);
        } catch (e) {
            setAddMemberStatus({ error: e.message || "Failed to add", msg: "" });
        } finally {
            setAddingMember(false);
        }
    };

    const handleCheckout = async (payload) => {
        if (!selectedMember) return;
        setCheckoutLoading(true);
        try {
            const nameVal = memberInfo?.name || selectedMember?.name || "";
            const nameParts = nameVal.trim().split(/\s+/);
            const first_name = nameParts[0] || "Guest";
            const last_name = nameParts.slice(1).join(" ") || "Customer";

            const email = memberInfo?.email || selectedMember?.email || "";
            const phone = memberInfo?.phone || selectedMember?.phone || "";

            const addrObj = parseAddress(
                memberInfo?.billing_address || selectedMember?.address || "",
                memberInfo?.pin_code
            );

            const billing = {
                first_name,
                last_name,
                email,
                phone,
                address_1: addrObj.address_1,
                city: addrObj.city,
                state: addrObj.state,
                postcode: addrObj.postcode,
                country: addrObj.country
            };

            const shipping = {
                first_name,
                last_name,
                address_1: addrObj.address_1,
                city: addrObj.city,
                state: addrObj.state,
                postcode: addrObj.postcode,
                country: addrObj.country
            };

            const paymentMethodLower = (payload.paymentMethod || "").toLowerCase();
            const isCod = paymentMethodLower === "atstore" || paymentMethodLower === "cod";
            const payment_method = isCod ? "cod" : (paymentMethodLower || "online");
            const payment_method_title = isCod ? "Cash on Delivery" : (payload.paymentMethod || "Online Payment");

            const coupon_code = payload.coupon ? (payload.coupon.coupon_code || payload.coupon.code || "") : "";
            const gift_card = payload.giftCardCode || (payload.giftCard ? (payload.giftCard.code || payload.giftCard.gift_card || "") : "");

            let order_type = "walk-in";
            if (payload.orderType === "DELIVERY") order_type = "delivery";
            if (payload.orderType === "VIRTUAL") order_type = "virtual";

            const items = cartItems.map(item => ({
                product_id: Number(item.id),
                quantity: Number(item.quantity || 1),
                issue_or_return: item.status === "Returning" || item.status === "return" ? "return" : "issue",
                copy_post_id: Number(item.copyId || item.copy_post_id || 0)
            }));

            const checkoutPayload = {
                billing,
                shipping,
                payment_method,
                payment_method_title,
                coupon_code,
                gift_card: gift_card || "",
                customer_note: payload.customerNote || "",
                delivery_slot: payload.deliverySlot || "",
                order_type,
                items
            };

            const res = await checkoutOrder({ token, payload: checkoutPayload });
            
            if (res?.success === false || (res?.code && res?.code !== "success" && res?.message)) {
                throw new Error(res?.message || "Checkout failed");
            }

            showToast("Order placed successfully! 🎉");
            setCartItems([]);
            await handleMemberSelect(selectedMember);

        } catch (err) {
            console.error("Checkout error:", err);
            showToast(`⚠️ ${err.message || "An error occurred during checkout"}`);
        } finally {
            setCheckoutLoading(false);
        }
    };

    return (
        <div className="flex h-screen w-full bg-white overflow-hidden font-sans">
            {toast && (
                <div className="fixed top-5 right-5 z-[100] bg-gray-900 text-white text-sm px-4 py-3 rounded-lg shadow-lg animate-fade-in-down">
                    {toast}
                </div>
            )}

            {/* === LEFT PANEL: BOOKS === */}
            <div className="w-[28%] min-w-[300px] border-r border-gray-200 flex flex-col bg-gray-50/30 relative">
                {selectedMember && walkInType === "WALK_IN" ? (
                    <div className="flex-1 flex flex-col items-center justify-center p-6 text-center bg-gray-50/80 backdrop-blur-[1px] select-none">
                        <div className="bg-black/5 p-4 rounded-full mb-4">
                            <QrCodeIcon className="h-10 w-10 text-gray-700" />
                        </div>
                        <h3 className="text-sm font-bold text-gray-900 mb-1">Walk-In Mode Active</h3>
                        <p className="text-xs text-gray-500 max-w-[200px] leading-relaxed">
                            Manual search is disabled. Please use the Book Scanner on the right to scan and add books for issue or return.
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="p-4 border-b border-gray-200 bg-white flex gap-2">
                            <div className="flex-1 relative">
                                <input
                                    value={bookQuery}
                                    onChange={(e) => setBookQuery(e.target.value)}
                                    placeholder="Search Books..."
                                    className="w-full border border-gray-300 rounded-md pl-3 pr-8 py-2 text-sm focus:ring-1 focus:ring-black outline-none"
                                />
                                <MagnifyingGlassIcon className="w-4 h-4 absolute right-2.5 top-3 text-gray-400" />
                            </div>
                        </div>

                        <div ref={listContainerRef} className="flex-1 overflow-y-auto p-4 space-y-3">
                            {booksLoading && products.length === 0 && (
                                <div className="flex justify-center pt-8"><Loader /></div>
                            )}

                            {!booksLoading && products.length === 0 && (
                                <div className="text-center py-10 text-gray-400 text-sm">No books found</div>
                            )}

                            {products.map(book => (
                                <BookCard
                                    key={book.id}
                                    book={book}
                                    onSelect={() => handleBookSelect(book)}
                                    onIconClick={handleBookIconClick}
                                />
                            ))}

                            {loadingMore && <div className="flex justify-center py-4"><Loader size="sm" /></div>}
                        </div>
                    </>
                )}
            </div>

            {/* === RIGHT PANEL: MEMBERS === */}
            <div className="flex-1 h-full overflow-hidden bg-white p-6 flex flex-col relative">
                {!selectedMember ? (
                    <div className="w-full h-full flex flex-col">
                        <div className="flex gap-4 mb-4 items-center flex-shrink-0">
                            <div className="flex-1">
                                <SearchableMemberSelect
                                    members={rawMembers}
                                    value=""
                                    onChange={(memberId, memberObj) => {
                                        if (memberId && memberObj) {
                                            if (!rawMembers.some(x => String(x.id) === String(memberObj.id))) {
                                                setMembers(prev => {
                                                    const updated = [...prev, memberObj];
                                                    saveCache(CACHE_KEYS.MEMBERS, updated);
                                                    return updated;
                                                });
                                            }
                                            handleMemberSelect(memberObj);
                                        }
                                    }}
                                    placeholder="Search Member..."
                                />
                            </div>
                            <button
                                onClick={() => setIsAddMemberOpen(true)}
                                className="bg-black hover:bg-gray-800 text-white px-5 rounded-md flex items-center gap-2 h-11 text-sm font-medium"
                            >
                                <UserPlusIcon className="w-4 h-4" /> Add Member
                            </button>
                        </div>

                        {/* LIST LOGIC */}
                        {membersLoading && rawMembers.length === 0 ? (
                            <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
                                <Loader /> <p className="text-sm mt-3">Loading members...</p>
                            </div>
                        ) : (
                            <div className="flex-1 overflow-y-auto space-y-1">
                                {membersError && <div className="bg-red-50 text-red-600 p-3 rounded mb-2 text-center text-sm">{membersError}</div>}
                                {rawMembers.length === 0 && <div className="text-center py-20 text-gray-400">No members found</div>}
                                {rawMembers.slice(0, 20).map(m => (
                                    <MemberListItem
                                        key={m.id}
                                        member={m}
                                        onSelect={handleMemberSelect}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="h-full flex flex-col w-full relative">
                        {/* ✅ SYNC INDICATOR: Clean Text Only (No Loader) */}
                        {isSyncing && (
                            <div className="absolute top-6 inset-x-0 z-10 flex justify-center pointer-events-none">
                                <span className="bg-white/90 backdrop-blur-sm border shadow-sm px-4 py-1.5 rounded-full text-xs text-gray-500 font-medium animate-pulse">
                                    Syncing shelf...
                                </span>
                            </div>
                        )}

                        <MemberCheckout
                            selectedMember={selectedMember}
                            member={memberInfo}
                            shelfItems={shelfItems}
                            cartItems={cartItems}
                            onReturnBook={(book) => {
                                setCartItems(prev => prev.some(x => x.id === book.id) ? prev : [...prev, { ...book, status: "Returning" }]);
                            }}
                            onRemoveFromCart={(item) => setCartItems(prev => prev.filter(x => x.id !== (item?.id || item)))}
                            onCheckout={handleCheckout}
                            checkoutLoading={checkoutLoading}
                            onBack={() => setSelectedMember(null)}
                            onAddDamage={(book) => setActiveModal({ type: 'DAMAGE', data: book })}
                            walkInType={walkInType}
                            onWalkInTypeChange={setWalkInType}
                            onAddScannedBook={(book, status) => {
                                if (status === "Issuing" && book?.stock_status === "outofstock") {
                                    showToast("Cannot add out of stock books to cart");
                                    return;
                                }
                                setCartItems(prev => {
                                    if (prev.some(x => x.id === book.id)) return prev;
                                    return [...prev, { ...book, status }];
                                });
                            }}
                        />
                    </div>
                )}
            </div>

            {/* === MODALS === */}
            <ConfirmMemberModal
                open={activeModal.type === 'CONFIRM'}
                member={selectedMember}
                book={activeModal.data}
                onClose={closeModal}
                onContinue={() => {
                    if (activeModal.data) {
                        if (activeModal.data.stock_status === "outofstock") {
                            showToast("Cannot add out of stock books to cart");
                            closeModal();
                            return;
                        }
                        setCartItems(prev => [...prev, { ...activeModal.data, status: "Issuing" }]);
                        showToast("Book added to cart ✅");
                    }
                    closeModal();
                }}
            />
            <BookHistoryModal
                open={activeModal.type === 'HISTORY'}
                book={activeModal.data}
                onClose={closeModal}
                onAddDamage={(book, initialData) => {
                    const modifiedBook = {
                        ...book,
                        added_order_id: initialData.orderId,
                    };
                    const m = rawMembers.find(member => String(member.id) === String(initialData.memberId));
                    setDamageMember(m || null);
                    setActiveModal({ type: 'DAMAGE', data: modifiedBook });
                }}
            />
            <DamageModal
                open={activeModal.type === 'DAMAGE'}
                book={activeModal.data}
                member={damageMember || selectedMember}
                members={rawMembers}
                onClose={() => {
                    closeModal();
                    setDamageMember(null);
                }}
                onMemberResolve={(m) => {
                    if (!rawMembers.some(x => String(x.id) === String(m.id))) {
                        setMembers(prev => {
                            const updated = [...prev, m];
                            saveCache(CACHE_KEYS.MEMBERS, updated);
                            return updated;
                        });
                    }
                }}
                onSave={() => showToast("Damage saved ✅")}
            />
            {activeModal.type === 'SCANNER' && <ScannerModal onClose={closeModal} />}

            {isAddMemberOpen && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[70] p-4">
                    <div className="bg-white w-full max-w-md rounded-xl shadow-2xl p-6">
                        <h2 className="text-xl font-bold mb-6">Add New Member</h2>
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <input value={newMember.first_name} onChange={e => setNewMember(p => ({ ...p, first_name: e.target.value }))} placeholder="First Name" className="border rounded-lg px-3 py-2 w-full" />
                                <input value={newMember.last_name} onChange={e => setNewMember(p => ({ ...p, last_name: e.target.value }))} placeholder="Last Name" className="border rounded-lg px-3 py-2 w-full" />
                            </div>
                            <input value={newMember.email} onChange={e => setNewMember(p => ({ ...p, email: e.target.value }))} placeholder="Email" className="border rounded-lg px-3 py-2 w-full" />
                            <input value={newMember.phone} onChange={e => setNewMember(p => ({ ...p, phone: e.target.value }))} placeholder="Phone" className="border rounded-lg px-3 py-2 w-full" />
                        </div>
                        {(addMemberStatus.msg || addMemberStatus.error) && (
                            <div className={`mt-4 p-2 text-sm rounded ${addMemberStatus.error ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'}`}>
                                {addMemberStatus.msg || addMemberStatus.error}
                            </div>
                        )}
                        <div className="flex justify-end gap-3 mt-8">
                            <button onClick={() => setIsAddMemberOpen(false)} className="px-4 py-2 border rounded-lg">Cancel</button>
                            <button onClick={handleAddMember} disabled={addingMember} className="px-6 py-2 bg-black text-white rounded-lg">{addingMember ? "Adding..." : "Add"}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}